import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState
} from 'react';
import { UserImpersonation } from '@partnerconnect-monorepo/types/UserImpersonation';
import { removeLocalStorageImpersonationData } from '../../utils/impersonationStorage';
import {
  ImpersonatedUserEmail,
  ImpersonatedUserId
} from '../../constants/impersonateUser';
import { navigateToUrl } from '../../utils/navigation';

const UserImpersonationContext = createContext<UserImpersonation | undefined>(
  undefined
);

export function UserImpersonationProvider({ children }: PropsWithChildren) {
  const [impersonatedUserEmail, setImpersonatedUserEmail] = useState<
    string | null
  >(null);

  const startImpersonatingUser = (userEmail: string, id: number) => {
    setImpersonatedUserEmail(userEmail);
    localStorage.setItem(ImpersonatedUserId, id.toString());
    localStorage.setItem(ImpersonatedUserEmail, userEmail);
    navigateToUrl('/');
  };

  const stopImpersonating = () => {
    removeLocalStorageImpersonationData();
    setImpersonatedUserEmail(null);
    navigateToUrl('/');
  };

  const value: UserImpersonation = {
    startImpersonatingUser,
    impersonatedUserEmail,
    isImpersonating: !!impersonatedUserEmail,
    stopImpersonating
  };

  useEffect(() => {
    const impersonatedUserIdStorage = localStorage.getItem(ImpersonatedUserId);
    const impersonatedUserEmailStorage = localStorage.getItem(
      ImpersonatedUserEmail
    );
    if (impersonatedUserIdStorage && impersonatedUserEmailStorage) {
      setImpersonatedUserEmail(impersonatedUserEmailStorage);
    }
  }, []);

  return (
    <UserImpersonationContext.Provider value={value}>
      {children}
    </UserImpersonationContext.Provider>
  );
}

export function useUserImpersonation() {
  const context = useContext(UserImpersonationContext);
  if (context === undefined) {
    throw new Error(
      'useUserImpersonation must be used within UserImpersonationProvider'
    );
  }
  return context;
}
